import { useState, useDeferredValue } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Search } from "lucide-react";
import {
  useSuperadminTenants,
  useUpdateTenantPlan,
  useUpdateTenantTier,
  useUpdateTenantStatus,
  useLogImpersonation,
} from "@/hooks/use-superadmin";
import { useImpersonation } from "@/providers/impersonation-provider";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const PLANS = ["starter", "essential", "growth", "business", "enterprise", "msp"] as const;
const TIERS = ["silver", "gold", "platinum"] as const;

export default function SuperadminTenantsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDeferredValue(search);
  const [statusFilter, setStatusFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [page, setPage] = useState(0);
  const limit = 25;

  const { startImpersonation } = useImpersonation();

  const tenantsQuery = useSuperadminTenants({
    limit,
    offset: page * limit,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    plan: planFilter || undefined,
  });
  const updatePlan = useUpdateTenantPlan();
  const updateTier = useUpdateTenantTier();
  const updateStatus = useUpdateTenantStatus();
  const logImpersonation = useLogImpersonation();

  const tenants = tenantsQuery.data?.tenants ?? [];
  const total = tenantsQuery.data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  function handleImpersonate(tenantId: string, tenantName: string) {
    logImpersonation.mutate(tenantId);
    startImpersonation(tenantId, tenantName);
    navigate("/app");
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Building2 className="h-6 w-6 text-red-500" />
        <h1 className="text-2xl font-semibold tracking-tight">All Tenants</h1>
        <span className="text-sm text-muted-foreground">({total})</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search name or slug..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="h-9 rounded-md border border-input bg-background pl-9 pr-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="decommissioned">Decommissioned</option>
        </select>
        <select
          value={planFilter}
          onChange={(e) => { setPlanFilter(e.target.value); setPage(0); }}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All plans</option>
          {PLANS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>MSP Parent</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenantsQuery.isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 8 }).map((_, j) => (
                      <TableCell key={j}>
                        <div className="h-4 w-20 animate-pulse rounded bg-muted" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : tenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground">
                    No tenants found.
                  </TableCell>
                </TableRow>
              ) : (
                tenants.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell className="font-mono text-xs">{t.slug}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <select
                          value={t.plan}
                          onChange={(e) => updatePlan.mutate({ tenantId: t.id, plan: e.target.value })}
                          className="h-7 rounded border border-input bg-background px-2 text-xs"
                        >
                          {PLANS.map((p) => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                        {t.plan === "msp" && !t.parent_msp_id && (
                          <select
                            aria-label="Partner tier"
                            value={t.partner_tier ?? ""}
                            onChange={(e) => updateTier.mutate({ tenantId: t.id, tier: e.target.value })}
                            className="h-7 rounded border border-input bg-background px-2 text-xs"
                          >
                            <option value="" disabled>tier…</option>
                            {TIERS.map((tier) => (
                              <option key={tier} value={tier}>{tier}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={t.status === "active" ? "default" : "secondary"}>
                        {t.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{t.user_count}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {t.parent_msp_id ? t.parent_msp_id.slice(0, 8) + "..." : "\u2014"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(t.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleImpersonate(t.id, t.name)}
                          className="rounded px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
                        >
                          Enter
                        </button>
                        {t.status === "active" ? (
                          <button
                            onClick={() => {
                              if (window.confirm(`Decommission tenant "${t.name}"? This will disable the tenant.`)) {
                                updateStatus.mutate({ tenantId: t.id, status: "decommissioned" });
                              }
                            }}
                            className="rounded px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-500/10"
                          >
                            Decommission
                          </button>
                        ) : (
                          <button
                            onClick={() => updateStatus.mutate({ tenantId: t.id, status: "active" })}
                            className="rounded px-2 py-1 text-xs font-medium text-green-500 hover:bg-green-500/10"
                          >
                            Reactivate
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Showing {page * limit + 1}&ndash;{Math.min((page + 1) * limit, total)} of {total}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="rounded border px-3 py-1 text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              className="rounded border px-3 py-1 text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
